/**
 * The outlet this person is working on when the session is not bound to a
 * terminal (back office in a browser): picked in the top bar, remembered on this
 * device. One store, so the top bar and every screen agree.
 */
import { useSyncExternalStore } from 'react';

const KEY = 'qrkey.staff.outlet.v1';
const listeners = new Set<() => void>();

function read(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

let current: string | null = typeof window === 'undefined' ? null : read();

export const pickedOutletStore = {
  get: (): string | null => current,
  set(id: string | null) {
    current = id;
    try {
      if (id) window.localStorage.setItem(KEY, id);
      else window.localStorage.removeItem(KEY);
    } catch {
      // remembered for this tab only
    }
    listeners.forEach((l) => l());
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function usePickedOutlet(): string | null {
  return useSyncExternalStore(
    pickedOutletStore.subscribe,
    pickedOutletStore.get,
    pickedOutletStore.get,
  );
}

/** The session's outlet (terminal-bound), else the picked one if allowed, else the first. */
export function resolveOutlet(
  sessionOutletId: string | null | undefined,
  picked: string | null,
  available: readonly string[],
): string | null {
  if (sessionOutletId) return sessionOutletId;
  if (picked && available.includes(picked)) return picked;
  return available[0] ?? null;
}
